import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  InputBase,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  fetchInstructorDashboard,
  fetchAttendanceForSession,
  updateRegistrationStatus,
} from "../../api/backend";
import { createDmThread, sendMessagingMessage, getAhaEligibility } from "../../api/apiClient";
import AhaStatusStrip from "../../components/aha/AhaStatusStrip";
import ClassCertificatesPanel from "../../components/certificates/ClassCertificatesPanel";
import SendCertificatesBar from "../../components/certificates/SendCertificatesBar";
import { isAhaVisible } from "../../components/aha/ahaText";
import { RosterRow, ColHead, GroupHeader } from "./roster/RosterRow";
import { DS, AVATAR_GRADIENTS, PULSE_KEYFRAME } from "./roster/rosterDesign";
import {
  deriveResult, deriveMode, resultNote, filterTabsFor, groupResults, isReadyToSignOff,
} from "./roster/rosterModel";
import StudentProfileDrawer from "./StudentProfileDrawer";
import BulkSignOffBar from "../../components/roster/BulkSignOffBar";
import { useTenant } from "../../contexts/TenantContext";
import { useTheme, alpha } from "@mui/material/styles";

const AHA_READY_FILTER = "aha-ready";
const isAhaReady = (aha) => !!aha && aha.eligible;
const regName = (reg) => reg.user?.name || reg.user?.username || `Student ${reg.userId}`;
const AHA_POLL_MS = 4000;

export default function RosterTab({
  course,
  sessions,
  checklists,
  courseInstructors = [],
  canRegisterStudents = false,
}) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tAccent = theme.palette.warning.main;
  const tAccent50 = alpha(tAccent, 0.10);
  const { id: courseId } = useParams();
  const { user } = useTenant();

  const [registrations, setRegistrations] = useState([]);
  const [liveAttendance, setLiveAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerStudent, setDrawerStudent] = useState(null);
  // Students ticked for bulk sign-off: userId (string) → name.
  const [selected, setSelected] = useState({});
  // AHA eCards: server-computed readiness per student plus the class-level setup state.
  const [aha, setAha] = useState(null);
  const [ahaLoadError, setAhaLoadError] = useState(false);
  // Bumped whenever the roster's AHA status changes, so the Send certificates summary re-reads too.
  const [certificatesVersion, setCertificatesVersion] = useState(0);
  const [quickDmOpen, setQuickDmOpen] = useState(false);
  const [quickDmStudent, setQuickDmStudent] = useState(null);
  const [quickDmThread, setQuickDmThread] = useState(null);
  const [quickDmText, setQuickDmText] = useState("");
  const [quickDmLoading, setQuickDmLoading] = useState(false);
  const [quickDmSending, setQuickDmSending] = useState(false);
  const [quickDmError, setQuickDmError] = useState("");
  const [quickDmNotice, setQuickDmNotice] = useState("");

  function openStudentProfile(userId, name) {
    setDrawerStudent({ userId, name });
    setDrawerOpen(true);
  }

  const liveSession = sessions.find((s) => s.enabled === true);

  // quiet: re-read after a sign-off without swapping the roster for a spinner.
  const loadRoster = React.useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    const calls = [fetchInstructorDashboard(courseId)];
    if (liveSession) calls.push(fetchAttendanceForSession(liveSession.id));

    return Promise.allSettled(calls).then(([regsRes, attRes]) => {
      if (regsRes.status === "fulfilled" && Array.isArray(regsRes.value)) {
        setRegistrations(regsRes.value);
      } else {
        setError("Failed to load roster.");
      }
      if (attRes && attRes.status === "fulfilled" && Array.isArray(attRes.value)) {
        setLiveAttendance(attRes.value);
      }
      setLoading(false);
    });
  }, [courseId, liveSession?.id]); // eslint-disable-line

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const loadAha = React.useCallback(() => {
    return getAhaEligibility(courseId)
      .then((data) => {
        setAha(data);
      })
      .then(() => setAhaLoadError(false))
      .catch((err) => {
        // AHA being unavailable must never break the roster. 403/404 mean AHA simply doesn't apply to this
        // viewer or class; anything else is a failure worth saying out loud rather than silently hiding the column.
        const status = err?.response?.status;
        setAha(null);
        setAhaLoadError(status !== 403 && status !== 404);
      });
  }, [courseId]);

  useEffect(() => {
    loadAha();
  }, [loadAha]);

  // Cards are sent in the background: while any request is queued or sending, re-read until AHA has answered.
  const ahaInFlight = (aha?.students || []).some((st) => st.issuanceState === "PENDING" || st.issuanceState === "SENDING");
  useEffect(() => {
    if (!ahaInFlight) return undefined;
    const timer = setTimeout(loadAha, AHA_POLL_MS);
    return () => clearTimeout(timer);
  }, [ahaInFlight, aha, loadAha]);

  const ahaEnabled = isAhaVisible(aha);
  const ahaByUser = new Map((aha?.students || []).map((s) => [String(s.userId), s]));
  const canConfigureAha = !!(user?.roles || []).some((r) => ["COURSE_COORDINATOR", "ADMIN", "GLOBAL_ADMIN"].includes(r));
  const refreshCertificates = () => {
    loadAha();
    setCertificatesVersion((v) => v + 1);
  };

  // Closing the class is what makes certificates sendable: re-read them when it flips to complete, no page refresh.
  const lifecycleStatus = course?.lifecycleStatus;
  const prevLifecycleStatus = React.useRef(lifecycleStatus);
  useEffect(() => {
    if (prevLifecycleStatus.current !== lifecycleStatus && lifecycleStatus === "COMPLETED") {
      refreshCertificates();
    }
    prevLifecycleStatus.current = lifecycleStatus;
  }, [lifecycleStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkedInIds = new Set(
    liveAttendance
      .filter((a) => a.attended || a.status === "Present" || a.checkedInAt)
      .map((a) => String(a.userId))
  );

  const enrolledRegs = registrations.filter((r) => r.status !== "WAITLISTED");
  const waitlistedRegs = registrations
    .filter((r) => r.status === "WAITLISTED")
    .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));

  const enrolled = enrolledRegs.length;

  // One result per enrolled student (signed / ready / failed / progress, or on-track / behind / at-risk while
  // sessions are still running), and the mode that follows from them all.
  const resultByUser = new Map(enrolledRegs.map((reg) => {
    const isSignedIn = !!liveSession && checkedInIds.has(String(reg.userId || ""));
    return [String(reg.userId), deriveResult(reg, { isSignedIn })];
  }));
  const resultOf = (reg) => resultByUser.get(String(reg.userId));
  const mode = deriveMode([...resultByUser.values()]);

  useEffect(() => {
    if (!ahaEnabled && filter === AHA_READY_FILTER) setFilter("all");
  }, [ahaEnabled, filter]);

  const filtered = enrolledRegs.filter((reg) => {
    const name = (reg.user?.name || reg.user?.username || "").toLowerCase();
    const email = (reg.user?.email || "").toLowerCase();
    const q = search.toLowerCase();
    const matchSearch = !search || name.includes(q) || email.includes(q);
    const matchFilter =
      filter === "all" ||
      (filter === AHA_READY_FILTER ? isAhaReady(ahaByUser.get(String(reg.userId))) : filter === resultOf(reg));
    return matchSearch && matchFilter;
  });

  const selectedStudents = Object.entries(selected).map(([userId, name]) => ({ userId, name }));
  const shownIds = filtered.map((reg) => String(reg.userId));
  const shownSelected = shownIds.filter((id) => selected[id] !== undefined).length;
  const toggleSelect = (userId, name) => setSelected((prev) => {
    const key = String(userId);
    if (prev[key] !== undefined) {
      const { [key]: _removed, ...rest } = prev;
      return rest;
    }
    return { ...prev, [key]: name };
  });
  const toggleSelectShown = () => setSelected((prev) => {
    if (shownIds.length > 0 && shownSelected === shownIds.length) {
      return Object.fromEntries(Object.entries(prev).filter(([id]) => !shownIds.includes(id)));
    }
    const added = Object.fromEntries(filtered.map((reg) => [String(reg.userId),
      regName(reg)]));
    return { ...prev, ...added };
  });
  const readyRegs = enrolledRegs.filter(isReadyToSignOff);
  // A sign-off asked for from a row or the banner: BulkSignOffBar opens its confirmation for exactly these students.
  const [signOffRequest, setSignOffRequest] = useState(null);
  const requestSignOff = (students) => setSignOffRequest((prev) => ({ id: (prev?.id || 0) + 1, students }));
  // After any sign-off: the roster's status, AHA readiness and the Send certificates summary all change.
  const afterSignOff = () => {
    loadRoster(true);
    refreshCertificates();
  };

  const countResult = (key) => enrolledRegs.filter((r) => resultOf(r) === key).length;
  const filterCounts = {
    all: enrolledRegs.length,
    ...Object.fromEntries(["signed-in", "on-track", "behind", "at-risk", "ready", "signed", "failed", "progress"]
      .map((key) => [key, countResult(key)])),
    [AHA_READY_FILTER]: enrolledRegs.filter((r) => isAhaReady(ahaByUser.get(String(r.userId)))).length,
  };
  const baseTabs = filterTabsFor(mode, filterCounts);
  const filterTabs = ahaEnabled ? [...baseTabs, { key: AHA_READY_FILTER, label: "Ready for AHA", aha: true }] : baseTabs;
  // A filter that no longer exists in this mode (e.g. "Behind" once the class wraps up) falls back to All.
  const tabKeys = filterTabs.map((t) => t.key).join(",");
  useEffect(() => {
    if (filter !== "all" && !tabKeys.split(",").includes(filter)) setFilter("all");
  }, [filter, tabKeys]);
  const groups = groupResults(
    filtered.map((reg) => ({ reg, result: resultOf(reg) })), mode, filter);
  const signedCount = countResult("signed");
  const failedCount = countResult("failed");

  const closeQuickDm = () => {
    if (quickDmSending) return;
    setQuickDmOpen(false);
    setQuickDmStudent(null);
    setQuickDmThread(null);
    setQuickDmText("");
    setQuickDmError("");
    setQuickDmLoading(false);
  };

  const handleDmStudent = async (reg) => {
    const studentId = reg.userId;
    const studentName = regName(reg);

    setQuickDmStudent({ id: studentId, name: studentName });
    setQuickDmThread(null);
    setQuickDmText("");
    setQuickDmError("");
    setQuickDmOpen(true);
    setQuickDmLoading(true);

    try {
      const thread = await createDmThread(Number(courseId), Number(studentId));
      setQuickDmThread(thread);
    } catch (err) {
      console.error("Failed to create DM thread:", err);
      setQuickDmError("Could not start this message. Please try again.");
    } finally {
      setQuickDmLoading(false);
    }
  };

  const handleSendQuickDm = async () => {
    const message = quickDmText.trim();
    if (!message || !quickDmThread?.id || quickDmSending) return;

    setQuickDmSending(true);
    setQuickDmError("");
    try {
      await sendMessagingMessage(quickDmThread.id, message);
      setQuickDmNotice(`Message sent to ${quickDmStudent?.name || "student"}.`);
      setQuickDmOpen(false);
      setQuickDmStudent(null);
      setQuickDmThread(null);
      setQuickDmText("");
    } catch (err) {
      console.error("Failed to send quick message:", err);
      setQuickDmError("Message failed to send. Please try again.");
    } finally {
      setQuickDmSending(false);
    }
  };

  const handleMoveToRoster = async (reg) => {
    try {
      const updated = await updateRegistrationStatus(courseId, reg.id, { status: "ENROLLED" });
      // Only after the server accepted it (409 when the class is full): never optimistic.
      const status = updated?.enrollmentStatus || "ENROLLED";
      setRegistrations((prev) =>
        prev.map((r) => r.id === reg.id ? { ...r, status } : r)
      );
      setError(null);
    } catch (err) {
      const data = err?.response?.data;
      setError(err?.response?.status === 409
        ? (data?.message || "The class is full, so this student can't be moved to the roster yet.")
        : (data?.message || "Couldn't move the student to the roster. Please try again."));
    }
  };

  return (
    <Box sx={{ mt: 0 }}>
      <style>{PULSE_KEYFRAME}</style>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Roster card */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${DS.line}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
          {/* Toolbar */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto auto" },
              gap: "12px",
              alignItems: "center",
              px: "18px",
              py: "14px",
              borderBottom: `1px solid ${DS.line2}`,
              bgcolor: "var(--color-surface-canvas-light)",
            }}
          >
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: DS.ink,
                letterSpacing: "-.01em",
                display: "inline-flex",
                alignItems: "baseline",
                gap: 1,
                gridColumn: { xs: "1 / -1", md: "auto" },
                fontFamily: "var(--font-display)",
              }}
            >
              Class Roster
              <span style={{ fontSize: 12.5, fontWeight: 600, color: DS.ink3 }}>
                · {enrolled} students
              </span>
            </Typography>

            {/* Search */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "var(--color-surface-canvas-light)",
                border: `1px solid ${DS.line}`,
                borderRadius: "9px",
                px: 1.5,
                height: 34,
                color: DS.ink3,
                maxWidth: 320,
              }}
            >
              <SearchIcon sx={{ fontSize: 14 }} />
              <InputBase
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, fontSize: 13, color: DS.ink }}
              />
            </Box>

            {/* Filter buttons */}
            <Box
              sx={{
                display: "flex",
                gap: "2px",
                alignItems: "center",
                bgcolor: "var(--color-surface-canvas-light)",
                border: `1px solid ${DS.line}`,
                borderRadius: "9px",
                p: "3px",
                flexWrap: { xs: "wrap", sm: "nowrap" },
                overflowX: { xs: "visible", sm: "auto" },
                maxWidth: "100%",
                flexShrink: 1,
                minWidth: 0,
                rowGap: "3px",
              }}
            >
              {filterTabs.map((tab) => (
                <Box
                  key={tab.key}
                  component="button"
                  type="button"
                  aria-pressed={filter === tab.key}
                  onClick={() => setFilter(tab.key)}
                  sx={{
                    border: 0,
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: 12,
                    fontWeight: 600,
                    color: filter === tab.key ? "var(--color-on-primary)" : DS.ink2,
                    bgcolor: filter === tab.key ? tPrimary : "transparent",
                    px: 1.25,
                    py: "5px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "all .12s",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      bgcolor: filter === tab.key ? tPrimary : DS.surface2,
                    },
                  }}
                >
                  {tab.label}
                  <Box
                    sx={{
                      fontSize: "10.5px",
                      fontWeight: 700,
                      px: "5px",
                      py: "1px",
                      borderRadius: "999px",
                      bgcolor: filter === tab.key
                        ? "var(--color-on-dark-faint)"
                        : tab.aha ? "var(--color-success-bg)" : DS.line3,
                      color: filter === tab.key
                        ? "var(--color-on-primary)"
                        : tab.aha ? "var(--color-success)" : DS.ink3,
                    }}
                  >
                    {filterCounts[tab.key] ?? 0}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {ahaLoadError && (
            <Box sx={{ px: "18px", pt: "14px" }}>
              <Alert severity="warning" action={<Button color="inherit" size="small" onClick={loadAha}>Retry</Button>}>
                AHA eCard status couldn't be loaded.
              </Alert>
            </Box>
          )}
          <Box sx={{ px: "18px", pt: "14px" }}>
            {/* The class's certificates (coordinators change them; instructors see them), then the one-press send. */}
            <ClassCertificatesPanel classId={courseId} canEdit={canConfigureAha} excludeTypes={["AHA"]} onChanged={refreshCertificates} />
            {ahaEnabled && (
              <AhaStatusStrip classId={courseId} aha={aha} canOpenSetup={canConfigureAha} refreshKey={certificatesVersion} />
            )}
            <SendCertificatesBar classId={courseId} refreshKey={certificatesVersion} onSent={loadAha} />
            {readyRegs.length > 0 && (
              <Box data-testid="ready-to-sign-off-summary" sx={{ display: "flex", alignItems: "center", gap: 1.75,
                flexWrap: "wrap", mb: 1.5, px: 2, py: 1.5, borderRadius: "10px", bgcolor: DS.blueBg, color: DS.blue }}>
                <Typography sx={{ fontSize: 13.5, color: "inherit" }}>
                  <b>{readyRegs.length === 1 ? "1 student has passed" : `${readyRegs.length} students have passed`}</b>
                  {" "}and can be signed off.
                </Typography>
                <Button size="small" variant="contained" disableElevation onClick={() => requestSignOff(readyRegs.map((reg) => ({ userId: String(reg.userId), name: regName(reg) })))}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ ml: "auto", bgcolor: DS.blue, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase",
                    "&:hover": { bgcolor: DS.blueDark } }}>
                  {readyRegs.length === 1 ? "Sign off 1" : `Sign off all ${readyRegs.length}`}
                </Button>
              </Box>
            )}
            {mode === "complete" && (
              <Box data-testid="roster-complete-summary" sx={{ mb: 1.5, px: 2, py: 1.5, borderRadius: "10px",
                bgcolor: DS.greyBg, color: DS.ink, fontSize: 13.5 }}>
                <b>{signedCount} signed off.</b>{" "}
                {failedCount === 0 ? "Everyone passed."
                  : `${failedCount} student${failedCount === 1 ? "" : "s"} did not pass.`}
              </Box>
            )}
            <BulkSignOffBar classId={courseId} selected={selectedStudents} onClear={() => setSelected({})}
              request={signOffRequest} onDone={() => { setSelected({}); afterSignOff(); }} />
          </Box>

          {/* Column headers + rows — scrollable on mobile */}
          <Box sx={{ overflowX: "auto" }}>
            <ColHead ahaEnabled={ahaEnabled} selectAll={{
              checked: shownIds.length > 0 && shownSelected === shownIds.length,
              indeterminate: shownSelected > 0 && shownSelected < shownIds.length,
              disabled: shownIds.length === 0,
              onChange: toggleSelectShown,
            }} />
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary" fontSize={14}>
                  {search ? "No students match your search." : "No students enrolled."}
                </Typography>
              </Box>
            ) : (
              (groups || [{ key: "all", items: filtered.map((reg) => ({ reg, result: resultOf(reg) })) }]).map((group) => (
                <React.Fragment key={group.key}>
                  {groups && <GroupHeader group={group} />}
                  {group.items.map(({ reg, result }, idx) => (
                    <RosterRow
                      key={reg.id || reg.userId}
                      reg={reg}
                      index={idx}
                      result={result}
                      note={resultNote(reg, result)}
                      onProfileClick={openStudentProfile}
                      onDm={handleDmStudent}
                      onSignOff={requestSignOff}
                      ahaEnabled={ahaEnabled}
                      aha={ahaByUser.get(String(reg.userId))}
                      classId={courseId}
                      canResolveAha={!!aha?.canResolve}
                      onAhaResolved={refreshCertificates}
                      selected={selected[String(reg.userId)] !== undefined}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: "18px",
              py: "12px",
              borderTop: `1px solid ${DS.line2}`,
              bgcolor: "var(--color-surface-canvas-light)",
            }}
          >
            <Typography sx={{ fontSize: "12.5px", color: DS.ink3 }}>
              Showing {filtered.length} of {enrolled} enrolled students
            </Typography>
          </Box>
      </Paper>

      {/* Waitlist panel */}
      {waitlistedRegs.length > 0 && (
        <Paper
          elevation={0}
          sx={{ border: `1px solid ${DS.line}`, borderRadius: "16px", overflow: "hidden", mt: "16px" }}
        >
          {/* Head */}
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: "18px", py: "13px", borderBottom: `1px solid ${DS.line2}`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "11px" }}>
              <Box sx={{
                width: 28, height: 28, borderRadius: "8px",
                bgcolor: tAccent50, color: tAccent,
                display: "grid", placeItems: "center",
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </Box>
              <Typography sx={{ fontSize: "15px", fontWeight: 700, letterSpacing: "-.01em", color: DS.ink, m: 0, fontFamily: "var(--font-display)" }}>
                Waitlist
              </Typography>
              <Box sx={{
                fontSize: "11px", fontWeight: 700, px: "7px", py: "2px", borderRadius: "6px",
                bgcolor: tAccent50, color: tAccent,
              }}>
                {waitlistedRegs.length}
              </Box>
            </Box>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box component="thead">
                <Box component="tr">
                  {["Student", "Joined Waitlist", "Position", ""].map((h) => (
                    <Box key={h} component="th" sx={{
                      fontSize: "11px", fontWeight: 700, letterSpacing: ".07em",
                      textTransform: "uppercase", color: DS.ink4,
                      px: "18px", py: "9px", textAlign: "left",
                      borderBottom: `1px solid ${DS.line2}`, whiteSpace: "nowrap",
                    }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {waitlistedRegs.map((reg, i) => {
                  const name = reg.user?.name || reg.user?.username || "Student";
                  const email = reg.user?.email || "";
                  const initials = name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
                  const grad = AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length];
                  const joined = reg.createdAt
                    ? new Date(reg.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : "—";
                  return (
                    <Box key={reg.id} component="tr" sx={{
                      borderBottom: i === waitlistedRegs.length - 1 ? 0 : `1px solid ${DS.line2}`,
                      "&:hover": { bgcolor: DS.surface2 },
                    }}>
                      <Box component="td" sx={{ px: "18px", py: "13px" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <Avatar sx={{ width: 34, height: 34, background: grad, fontSize: "12px", fontWeight: 800, flexShrink: 0 }}>
                            {initials}
                          </Avatar>
                          <Box>
                            <Box sx={{ fontSize: "14px", fontWeight: 700, color: DS.ink }}>{name}</Box>
                            <Box sx={{ fontSize: "12.5px", color: DS.ink3 }}>{email}</Box>
                          </Box>
                        </Box>
                      </Box>
                      <Box component="td" sx={{ px: "18px", py: "13px", fontSize: "13px", color: DS.ink3 }}>
                        {joined}
                      </Box>
                      <Box component="td" sx={{ px: "18px", py: "13px" }}>
                        <Box component="span" sx={{
                          display: "inline-flex", alignItems: "center", gap: "5px",
                          fontSize: "11px", fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase",
                          px: "9px", py: "3px", borderRadius: "999px",
                          bgcolor: tAccent50, color: tAccent,
                        }}>
                          <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: tAccent }} />
                          #{i + 1}
                        </Box>
                      </Box>
                      <Box component="td" sx={{ px: "18px", py: "13px", textAlign: "right" }}>
                        {canRegisterStudents && (
                          <Box component="button" onClick={() => handleMoveToRoster(reg)} sx={{
                            height: 32, px: "13px", borderRadius: "9px",
                            border: `1.5px solid ${DS.line}`, bgcolor: "var(--color-surface-canvas-light)",
                            fontFamily: "inherit", fontSize: "12.5px", fontWeight: 700,
                            color: DS.ink2, cursor: "pointer", whiteSpace: "nowrap",
                            "&:hover": { bgcolor: DS.surface2, color: DS.ink },
                          }}>
                            Move to Roster
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      <StudentProfileDrawer
        open={drawerOpen}
        courseId={courseId}
        studentId={drawerStudent?.userId}
        studentName={drawerStudent?.name}
        onClose={() => setDrawerOpen(false)}
        onSignOff={afterSignOff}
      />

      <Dialog
        open={quickDmOpen}
        onClose={closeQuickDm}
        fullWidth
        maxWidth="xs"
        aria-labelledby="quick-message-title"
      >
        <DialogTitle id="quick-message-title" sx={{ fontFamily: "var(--font-display)" }}>
          Message {quickDmStudent?.name || "student"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {quickDmError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {quickDmError}
            </Alert>
          )}
          <TextField
            label="Message"
            placeholder="Type a quick message..."
            value={quickDmText}
            onChange={(e) => setQuickDmText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            autoFocus
            disabled={quickDmLoading || quickDmSending || !quickDmThread}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeQuickDm} disabled={quickDmSending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendQuickDm}
            disabled={quickDmLoading || quickDmSending || !quickDmThread || !quickDmText.trim()}
          >
            {quickDmSending ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(quickDmNotice)}
        autoHideDuration={3000}
        onClose={() => setQuickDmNotice("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setQuickDmNotice("")}>
          {quickDmNotice}
        </Alert>
      </Snackbar>
    </Box>
  );
}
